import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router'
import { OnboardingShell } from '@/onboarding/OnboardingShell'
import { startYandexOAuth, getYandexOAuthStatus } from '@/api/yandex'
import { bookmarkStep } from '@/api/onboarding'
import { usePinnedChannelUUID } from '@/onboarding/usePinnedChannelUUID'
import { track, trackError } from '@/lib/analytics'

export function YandexConnectPage() {
  const navigate = useNavigate()
  const channelUUID = usePinnedChannelUUID()
  const [alreadyConnected, setAlreadyConnected] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!channelUUID) return
    let cancelled = false
    getYandexOAuthStatus(channelUUID)
      .then((res) => {
        if (!cancelled) setAlreadyConnected(res.hasYandexAuth)
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [channelUUID])

  async function handleConnect() {
    if (!channelUUID) {
      setError('Канал не выбран. Вернитесь и выберите канал.')
      return
    }
    setSubmitting(true)
    setError(null)
    try {
      const res = await startYandexOAuth(channelUUID)
      // Намеренный click — фиксируем перед редиректом на Я. Это позволит мерить
      // конверсию «нажал на кнопку» → «вернулся с OAuth» (есть yandex_connected).
      track('yandex_oauth_started', { message: 'клик на «Подключить Я.Директ»' })
      window.location.href = res.oauthUrl
    } catch (e) {
      trackError(e, 'start-yandex-oauth')
      setError(e instanceof Error ? e.message : 'Не удалось запустить OAuth')
      setSubmitting(false)
    }
  }

  function handleSkip() {
    // «У меня нет Я.Директа» — без OAuth-токена нельзя пройти ads-флоу
    // (ad-gen-prepare упадёт на /generate). Меняем ветку на analytics → дашборд.
    track('yandex_skipped', { message: 'юзер пропустил подключение Я.Директа' })
    bookmarkStep(null, 'analytics')
    navigate('/dashboard?welcome=1')
  }

  function handleSkipForward() {
    bookmarkStep('yandex-done')
    navigate('/onboarding/yandex-done', { replace: true })
  }

  return (
    <OnboardingShell
      step="yandex-connect"
      footer={
        <>
          <button className="foot-back" onClick={() => navigate(-1)}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M15 18l-6-6 6-6" />
            </svg>
            Назад
          </button>
          <div className="foot-spacer" />
          <button className="foot-next" onClick={handleConnect} disabled={submitting}>
            {submitting ? 'Переходим в Яндекс…' : 'Подключить'}
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 3h7v7M21 3l-9 9M21 14v6a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h6" />
            </svg>
          </button>
        </>
      }
    >
      <div className="yc-grid">
        <div>
          <p className="stage-eyebrow">
            <span className="stage-eyebrow-pill">Реклама · подключение</span>
          </p>
          <h1 className="stage-title">
            Подключим <em>Яндекс&nbsp;Директ</em>
          </h1>
          <p className="stage-sub">
            Авторизация через ваш аккаунт Яндекса. <b>Мы только создаём кампании в&nbsp;вашем кабинете&nbsp;— деньги не&nbsp;списываем, доступ к&nbsp;платежам не&nbsp;получаем.</b> Не&nbsp;храним пароль&nbsp;— только токен доступа, его можно отозвать в&nbsp;один клик.
          </p>

          {alreadyConnected && (
            <div className="already-connected-banner">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 6L9 17l-5-5" />
              </svg>
              <div className="ac-text">
                <b>Я.Директ уже подключён к этому каналу.</b> Можно переподключить через другой аккаунт или сразу перейти дальше.
              </div>
              <button className="ac-skip" onClick={handleSkipForward}>
                Перейти дальше →
              </button>
            </div>
          )}

          <div className="step-card">
            <ul className="step-list">
              <li className="step-row">
                <div className="step-num">1</div>
                <div className="step-content">
                  <div className="step-text">Откройте браузер и войдите в аккаунт Яндекса</div>
                  <div className="step-sub">Используйте тот аккаунт, на который оформите рекламный кабинет.</div>
                </div>
              </li>
              <li className="step-row">
                <div className="step-num">2</div>
                <div className="step-content">
                  <div className="step-text">Нажмите «Подключить» ниже</div>
                  <div className="step-sub">Перейдёте на страницу авторизации Яндекса.</div>
                </div>
              </li>
              <li className="step-row">
                <div className="step-num">3</div>
                <div className="step-content">
                  <div className="step-text">Подтвердите доступ TeleGraphyx</div>
                  <div className="step-sub">
                    Вернётесь сюда автоматически. Также мы <b>создадим счётчик Метрики и цели для аналитики</b>.
                  </div>
                </div>
              </li>
            </ul>

            <div className="check-status-row idle">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="9" />
                <path d="M12 8v4M12 16v.01" />
              </svg>
              <div>Если у вас ещё нет рекламного кабинета Я.Директа&nbsp;— создадим вместе на следующем шаге.</div>
            </div>
          </div>

          {error && <div className="onb-error-inline">{error}</div>}
        </div>

        <div>
          <p className="helper-eye">Что увидите в браузере</p>
          <div className="oauth-mock">
            <div className="oauth-mock-bar">
              <span /> <span /> <span />
              <div className="oauth-mock-url">oauth.yandex.ru</div>
            </div>
            <div className="oauth-mock-body">
              <div className="oauth-mock-logo">
                <svg viewBox="0 0 24 24" fill="#FC3F1D">
                  <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10 10-4.5 10-10S17.5 2 12 2zm1.7 16h-1.9V9.5h-1.5l-.4 7.5H8.5l-.5-9h2.7l.5 8 1.4-8H14L13.7 18z" />
                </svg>
              </div>
              <div className="oauth-mock-app">
                <div className="oauth-mock-app-avatar">T</div>
                <div>
                  <div className="oauth-mock-app-name">TeleGraphyx запрашивает доступ</div>
                  <div className="oauth-mock-app-handle">от имени <b>вашего аккаунта</b></div>
                </div>
              </div>
              <div className="oauth-mock-perms">
                <PermItem>Доступ к кампаниям Я.Директа</PermItem>
                <PermItem>Создание счётчика Метрики</PermItem>
                <PermItem>Передача целей и конверсий</PermItem>
              </div>
              <div className="oauth-mock-cta">Разрешить</div>
              <div className="oauth-mock-cancel">Отменить</div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .yc-grid {
          display: grid;
          grid-template-columns: minmax(0, 1fr) 380px;
          gap: 32px;
          max-width: 1100px;
        }
        @media (max-width: 1024px) {
          .yc-grid { grid-template-columns: 1fr; }
        }
        .step-card {
          background: var(--tg-surface, #fff);
          border: 1px solid var(--tg-line-2, #f0f0f4);
          border-radius: 16px;
          overflow: hidden;
          max-width: 720px;
        }
        .step-list { list-style: none; margin: 0; padding: 0; }
        .step-row {
          display: grid;
          grid-template-columns: 36px 1fr;
          gap: 18px;
          padding: 22px 28px;
          align-items: flex-start;
          border-bottom: 1px solid var(--tg-line-2, #f0f0f4);
        }
        .step-num {
          width: 32px;
          height: 32px;
          border-radius: 50%;
          background: var(--tg-blue-bg, #ebf2ff);
          color: var(--tg-blue-press, #0066cc);
          font-weight: 800;
          font-size: 14px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .step-text {
          font-weight: 600;
          font-size: 15px;
          color: var(--tg-ink, #111);
          margin-bottom: 4px;
        }
        .step-sub {
          font-size: 12.5px;
          color: var(--tg-mute, #8f9eb0);
          line-height: 1.45;
        }
        .step-sub b { color: var(--tg-ink-2, #333); font-weight: 600; }

        .check-status-row {
          padding: 18px 28px;
          display: flex;
          align-items: center;
          gap: 14px;
          font-size: 13.5px;
          color: var(--tg-ink-2, #333);
        }
        .check-status-row.idle {
          background: var(--tg-surface-2, #f5f5f9);
        }
        .check-status-row.idle svg {
          width: 16px; height: 16px;
          color: var(--tg-mute, #8f9eb0);
          flex-shrink: 0;
        }
        .check-status-row.info {
          background: var(--tg-blue-bg, #ebf2ff);
        }
        .check-status-row.info .pip {
          width: 8px; height: 8px;
          border-radius: 50%;
          background: var(--tg-blue, #0080ff);
          animation: tg-pulse-pip 1.6s ease-out infinite;
          flex-shrink: 0;
        }
        @keyframes tg-pulse-pip {
          0% { box-shadow: 0 0 0 0 rgba(0,128,255,0.5); }
          100% { box-shadow: 0 0 0 8px rgba(0,128,255,0); }
        }
        .check-status-row.warn {
          background: rgba(255, 184, 0, 0.12);
        }
        .check-status-row.warn svg {
          width: 16px; height: 16px;
          color: rgb(180, 130, 0);
          flex-shrink: 0;
        }
        .check-status-row b {
          color: var(--tg-ink, #111);
          font-weight: 700;
        }

        .helper-eye {
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          color: var(--tg-mute, #8f9eb0);
          margin: 0 0 10px;
        }

        .oauth-mock {
          background: var(--tg-surface, #fff);
          border: 1px solid var(--tg-line-2, #f0f0f4);
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 8px 24px -8px rgba(38,40,45,0.1);
        }
        .oauth-mock-bar {
          height: 32px;
          background: var(--tg-surface-2, #f5f5f9);
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 0 12px;
          border-bottom: 1px solid var(--tg-line-2, #f0f0f4);
        }
        .oauth-mock-bar > span {
          width: 9px;
          height: 9px;
          border-radius: 50%;
          background: var(--tg-line-3, #d0d0d8);
        }
        .oauth-mock-url {
          margin-left: 12px;
          padding: 3px 10px;
          background: white;
          border-radius: 6px;
          font-size: 10px;
          color: var(--tg-mute, #8f9eb0);
          font-family: ui-monospace, SFMono-Regular, monospace;
        }
        .oauth-mock-body {
          padding: 22px;
          display: flex;
          flex-direction: column;
          gap: 14px;
        }
        .oauth-mock-logo {
          width: 36px;
          height: 36px;
          border-radius: 10px;
          background: rgba(252, 63, 29, 0.1);
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .oauth-mock-logo svg { width: 22px; height: 22px; }
        .oauth-mock-app {
          display: flex;
          align-items: center;
          gap: 10px;
        }
        .oauth-mock-app-avatar {
          width: 36px;
          height: 36px;
          border-radius: 50%;
          background: linear-gradient(135deg, var(--tg-blue, #0080ff), var(--tg-blue-press, #0066cc));
          color: white;
          font-weight: 800;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .oauth-mock-app-name {
          font-weight: 700;
          font-size: 13px;
          color: var(--tg-ink, #111);
        }
        .oauth-mock-app-handle {
          font-size: 11px;
          color: var(--tg-mute, #8f9eb0);
        }
        .oauth-mock-app-handle b { color: var(--tg-ink-2, #333); font-weight: 600; }
        .oauth-mock-perms {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }
        .oauth-mock-perm {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 10px;
          background: var(--tg-surface-2, #f5f5f9);
          border-radius: 8px;
          font-size: 12px;
          color: var(--tg-ink-2, #333);
        }
        .oauth-mock-perm svg {
          width: 14px; height: 14px;
          color: var(--tg-up, #00a860);
          flex-shrink: 0;
        }
        .oauth-mock-cta {
          padding: 11px 14px;
          background: #FC3F1D;
          color: white;
          font-weight: 700;
          font-size: 13px;
          text-align: center;
          border-radius: 8px;
        }
        .oauth-mock-cancel {
          padding: 8px 14px;
          color: var(--tg-mute, #8f9eb0);
          font-weight: 500;
          font-size: 12px;
          text-align: center;
        }

        .already-connected-banner {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 14px 18px;
          background: rgba(0, 168, 96, 0.08);
          border: 1px solid rgba(0, 168, 96, 0.25);
          border-radius: 12px;
          margin-bottom: 20px;
          max-width: 720px;
        }
        .already-connected-banner svg {
          width: 20px;
          height: 20px;
          color: var(--tg-up, #00a860);
          flex-shrink: 0;
        }
        .ac-text {
          flex: 1;
          font-size: 13px;
          color: var(--tg-ink-2, #333);
          line-height: 1.5;
        }
        .ac-text b { color: var(--tg-ink, #111); font-weight: 700; }
        .ac-skip {
          padding: 8px 14px;
          background: var(--tg-up, #00a860);
          color: white;
          border: 0;
          border-radius: 8px;
          font-family: inherit;
          font-weight: 700;
          font-size: 12px;
          cursor: pointer;
          flex-shrink: 0;
        }
        .ac-skip:hover { background: #00805a; }
        .onb-error-inline {
          margin-top: 12px;
          padding: 10px 14px;
          background: rgba(255, 64, 0, 0.08);
          border-radius: 8px;
          color: #c33;
          font-size: 12px;
        }
      `}</style>
    </OnboardingShell>
  )
}

function PermItem({ children }: { children: React.ReactNode }) {
  return (
    <div className="oauth-mock-perm">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20 6L9 17l-5-5" />
      </svg>
      {children}
    </div>
  )
}
